// Role-based access matrix.
//
// Every protected route is keyed here. Granting access = the user has at
// least one of the listed roles. Adding a new page means adding one line
// here — never sprinkling role checks throughout components.
//
// Roles come from the backend's AuthController::rolesFor() and are stored
// in authStore.user.roles. They're advisory in the UI; the backend
// enforces the same access via policies + middleware regardless of what
// the SPA shows.

export type Role =
  | 'platform_admin'      // legacy alias for super_admin
  | 'super_admin'
  | 'owner'
  | 'admin'               // branch admin (replaces the old 'manager')
  | 'manager'             // kept for back-compat with already-issued tokens
  | 'cashier'
  | 'sales_person'
  | 'procurement'
  | 'kitchen'
  | 'accountant'
  | 'bar'
  | 'investor';

// Convenience bundles to keep ACCESS compact.
const SUPER  : Role[] = ['platform_admin', 'super_admin'];
const TENANT : Role[] = ['owner', 'admin', 'manager']; // 'manager' = old token alias

export const ACCESS: Record<string, Role[]> = {
  // ─── Super admin surface ─────────────────────────────────────────────
  '/super':              SUPER,
  '/super/tenants':      SUPER,
  '/super/branches':     SUPER,
  '/super/investors':    SUPER,
  '/super/commission':   SUPER,
  '/super/users':        SUPER,
  '/super/settings':     SUPER,
  '/super/access':       SUPER,
  '/super/feedback':     SUPER,
  '/super/activity':     SUPER,

  // ─── Admin surface ───────────────────────────────────────────────────
  // Each path lists the roles permitted. Super admin is read-everywhere by
  // policy. Specialist staff (procurement / accountant / sales) get just
  // their slice.
  '/admin':              [...SUPER, ...TENANT, 'sales_person', 'accountant', 'procurement', 'bar'],
  // Bar department oversight — reachable directly by owner/accountant/super
  // admin (via impersonation, same as every other /admin/* page), not just
  // by an actual bar-role account landing here on login.
  '/admin/bar':          [...SUPER, ...TENANT, 'accountant', 'bar'],
  '/admin/orders':       [...SUPER, ...TENANT, 'sales_person', 'cashier', 'accountant'],
  '/admin/credit-orders':   [...SUPER, ...TENANT, 'sales_person', 'cashier', 'accountant'],
  '/admin/delivery-orders': [...SUPER, ...TENANT, 'sales_person', 'cashier', 'accountant'],
  '/admin/special-orders':  [...SUPER, ...TENANT, 'sales_person', 'cashier', 'accountant'],
  '/admin/menu':         [...TENANT],   // super admin doesn't add menu items (per spec)
  '/admin/feedback':     [...SUPER, ...TENANT, 'kitchen'],
  '/admin/branches':     [...SUPER, 'owner'],
  '/admin/employees':    [...SUPER, ...TENANT, 'accountant'],
  '/admin/customers':    [...SUPER, ...TENANT, 'cashier', 'sales_person', 'accountant'],
  '/admin/bulk-sms':        [...SUPER, ...TENANT],
  '/admin/credit-accounts':     [...SUPER, ...TENANT, 'accountant'],
  '/admin/credit-accounts/new': [...SUPER, ...TENANT, 'accountant'],
  // Per-company detail is /admin/credit-accounts/:id — there's no exact-match
  // entry for it (same pattern as /admin/reports/* sub-pages); canAccess()
  // falls through to "allow" for unmapped paths, gated only by the parent
  // /admin route guard plus the sidebar/UI not surfacing it to unauthorized roles.
  '/admin/attendance':   [...SUPER, ...TENANT],
  '/admin/expenses':     [...SUPER, ...TENANT, 'accountant', 'bar'],
  '/admin/procurement':  [...SUPER, ...TENANT, 'procurement', 'bar'],
  '/admin/inventory':    [...SUPER, ...TENANT, 'procurement'],
  '/admin/investors':    [...SUPER, 'owner', 'accountant'],
  // Commission is platform take-rate — super admin only, full stop. Every
  // tenant-side role (owner/admin/accountant/general staff) sees
  // net-of-sales figures elsewhere but never the commission line itself.
  // A platform admin impersonating a tenant still passes (isPlatformAdmin()
  // is tenant_id === null, unaffected by impersonation). Kept in sync with
  // COMMISSION_VISIBLE_ROLES in RecordsPage.tsx and
  // CommissionController::assertCanSeeCommission / ReportsController::canSeeCommission.
  '/admin/commission':   SUPER,
  '/admin/analytics':    [...SUPER, 'owner', 'accountant'],
  '/admin/reports':      [...SUPER, 'owner', 'accountant'],
  // canAccess() does exact-path lookup — without these, every child under
  // /admin/reports fell through to "unmapped path ⇒ allow" for ANY
  // authenticated role (including cashier/kitchen), regardless of the
  // parent route's list above. Mirrors the backend's matching
  // role:owner,accountant middleware on the same endpoints in
  // routes/api/v1.php.
  '/admin/reports/balance-sheet': [...SUPER, 'owner', 'accountant'],
  '/admin/reports/cash-flow':     [...SUPER, 'owner', 'accountant'],
  '/admin/reports/trial-balance': [...SUPER, 'owner', 'accountant'],
  // Not granted to 'bar': this report sums whole orders, which can mix
  // departments, so it can't be cleanly scoped to bar-only — it would leak
  // whole-tenant revenue/branch figures to bar staff. Bar's sales view is
  // /admin/records (department=bar), which is correctly item-level.
  '/admin/reports/orders':        [...SUPER, 'owner', 'accountant'],
  '/admin/reports/expenses':      [...SUPER, 'owner', 'accountant', 'bar'],
  '/admin/reports/procurement':   [...SUPER, 'owner', 'accountant', 'bar'],
  '/admin/reports/inventory':     [...SUPER, 'owner', 'accountant', 'bar'],
  '/admin/reports/customers':     [...SUPER, 'owner', 'accountant'],
  '/admin/reports/investors':     [...SUPER, 'owner', 'accountant'],
  '/admin/reports/employees':     [...SUPER, 'owner', 'accountant'],
  '/admin/records':      [...SUPER, ...TENANT, 'accountant', 'sales_person', 'bar', 'kitchen'],
  '/admin/settings':     [...SUPER, 'owner'],
  // Cancel/void/refund/tax-removal/hard-delete evidence — owner/admin can
  // review what happened; cashier/sales_person trigger the underlying
  // actions but don't browse the log. Matches the backend's
  // role:owner,admin,manager middleware on GET /audit-log.
  '/admin/audit-log':    [...SUPER, ...TENANT],

  // ─── Full-screen kiosks ──────────────────────────────────────────────
  // POS: owner/admin can ring sales when needed; cashier + sales_person
  // are the daily operators. Super admin is intentionally OUT of POS to
  // prevent cross-tenant sales artifacts — they impersonate via Tenants.
  '/pos':                ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
  '/pos/counter':        ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
  '/pos/credit':         ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
  '/pos/delivery':       ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
  '/pos/special':        ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
  // Bar's own point of sale — items filtered to the bar (drinks/beverages/
  // ice-cream); rings up like Counter otherwise. Bar staff + the owner/admin
  // who can already ring sales anywhere; not cashier/sales_person by default
  // — this is the bar department's own till, not the general floor POS.
  '/pos/bar':            ['owner', 'admin', 'manager', 'bar'],
  '/kitchen':            [...SUPER, ...TENANT, 'kitchen'],
  // Bar's live ticket board — mirrors /kitchen. Bar staff land here on
  // login (see AuthController::landingPathFor()); owner/admin/super
  // (impersonating) can drop in too, same as they can open the KDS.
  '/bar':                [...SUPER, ...TENANT, 'bar'],

  // ─── Investor portal ─────────────────────────────────────────────────
  '/investor':           [...SUPER, 'investor'],
};

/** True if the user's roles satisfy at least one of `required`. */
export function hasAnyRole(userRoles: string[] | undefined, required: Role[]): boolean {
  if (!userRoles) return false;
  return required.some((r) => userRoles.includes(r));
}

/**
 * Per-user permission overrides. Map of route path -> bool. Whatever's
 * present here wins over the role default. Anything NOT present falls
 * back to the role default.
 */
export type PermissionOverrides = Record<string, boolean> | null | undefined;

/**
 * This tenant's super-admin-set default access per role, from the
 * /super/access matrix — Record<role, Record<path, bool>>. Sits between the
 * global ACCESS map and a per-user override in priority: a tenant can
 * decide "every cashier here loses Reports" without touching each
 * cashier's individual permissions row. Baked into the login payload
 * (AuthController::login()'s role_overrides) — same staleness convention
 * as `roles`/`permissions`, requires re-login to pick up a change.
 */
export type RoleOverrides = Record<string, Record<string, boolean>> | null | undefined;

/** True if the user can visit this path. Unknown paths are allowed. */
export function canAccess(
  path: string,
  userRoles: string[] | undefined,
  overrides?: PermissionOverrides,
  roleOverrides?: RoleOverrides,
): boolean {
  // True super admins always pass everywhere — including cross-tenant
  // /super platform pages, by definition of the role.
  if (userRoles?.some((r) => ['super_admin', 'platform_admin'].includes(r))) {
    return true;
  }
  // An explicit per-user override always wins, checked BEFORE the owner
  // auto-grant below — otherwise a super-admin-set override on an owner's
  // account (e.g. "regulate the system" from /super/users) would be
  // silently ignored for every /admin/* path the auto-grant covers.
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, path)) {
    return overrides[path] === true;
  }
  // Tenant's per-role default (the /super/access matrix) — checked next,
  // after the per-user override (which is more specific) but before the
  // global ACCESS fallback below (which is less specific). Resolved
  // per-role: a role with an explicit entry uses it, a role without one
  // falls back to its own ACCESS-map default (roleDefaultAccessWithOwnerGrant
  // below folds in the same owner-auto-grant carve-out as the plain
  // fallback path so a role lacking an override isn't treated any
  // differently here than it would be without this whole feature). A user
  // holding several roles is granted if ANY role's resolution is true —
  // one role being explicitly denied must not veto access held via another
  // role the same user also carries.
  if (roleOverrides && userRoles?.length) {
    for (const role of userRoles) {
      const roleMap = roleOverrides[role];
      const explicit = roleMap && Object.prototype.hasOwnProperty.call(roleMap, path);
      const resolved = explicit ? roleMap![path] === true : roleDefaultAccessWithOwnerGrant(role, path);
      if (resolved) return true;
    }
    return false;
  }
  // Owners are auto-granted within their own tenant's /admin surface only —
  // this stops an owner accidentally locking themselves out of e.g.
  // /admin/settings when no explicit override exists. It must NOT extend to
  // /super (cross-tenant platform admin) or /investor (the investor
  // portal) — an owner is neither, and seeing those sidebar sections is a
  // tenant-isolation leak, not a convenience. It also must NOT extend to
  // /admin/commission — that page is super-admin-only by explicit policy
  // (owners see net-of-sales figures everywhere else, never the platform's
  // commission cut), so this blanket grant is deliberately carved out for
  // it rather than letting the auto-grant silently override the ACCESS map.
  if (userRoles?.includes('owner') && path.startsWith('/admin') && path !== '/admin/commission') {
    return true;
  }
  const required = ACCESS[path];
  if (!required) return true;
  return hasAnyRole(userRoles, required);
}

/**
 * Routes an editor sees in the per-employee permissions panel. Each entry
 * has a default — whether the role would normally grant access — and the
 * panel renders a checkbox starting at that default. Toggling stores an
 * explicit override.
 *
 * We exclude super-admin-only and identity-rooted routes (/investor, /super)
 * so the panel only surfaces choices that make sense to grant or revoke
 * for tenant staff.
 */
export const TOGGLEABLE_PATHS: Array<{ path: string; label: string; group: 'admin' | 'kiosk' | 'reports' | 'actions' }> = [
  // Admin surface
  { path: '/admin',              label: 'Dashboard',          group: 'admin'  },
  { path: '/admin/orders',       label: 'Orders',             group: 'admin'  },
  { path: '/admin/credit-orders',   label: 'Credit Orders',   group: 'admin'  },
  { path: '/admin/delivery-orders', label: 'Delivery Orders', group: 'admin'  },
  { path: '/admin/special-orders',  label: 'Special Orders',  group: 'admin'  },
  { path: '/admin/menu',         label: 'Menu',               group: 'admin'  },
  { path: '/admin/branches',     label: 'Branches',           group: 'admin'  },
  { path: '/admin/employees',    label: 'Employees',          group: 'admin'  },
  { path: '/admin/customers',    label: 'Customers',          group: 'admin'  },
  { path: '/admin/bulk-sms',        label: 'Bulk SMS',        group: 'admin'  },
  { path: '/admin/credit-accounts', label: 'Credit Accounts', group: 'admin'  },
  { path: '/admin/attendance',   label: 'Attendance',         group: 'admin'  },
  { path: '/admin/expenses',     label: 'Expenses',           group: 'admin'  },
  { path: '/admin/procurement',  label: 'Procurement',        group: 'admin'  },
  { path: '/admin/inventory',    label: 'Inventory',          group: 'admin'  },
  { path: '/admin/investors',    label: 'Investors',          group: 'admin'  },
  { path: '/admin/settings',     label: 'Settings',           group: 'admin'  },
  // Reports & analytics
  { path: '/admin/analytics',    label: 'Analytics',          group: 'reports' },
  { path: '/admin/reports',      label: 'Reports',            group: 'reports' },
  { path: '/admin/records',      label: 'Records',            group: 'reports' },
  // Full-screen kiosks
  { path: '/pos',                label: 'POS (sell)',         group: 'kiosk'  },
  { path: '/pos/counter',        label: 'POS · Counter',      group: 'kiosk'  },
  { path: '/pos/credit',         label: 'POS · Credit',       group: 'kiosk'  },
  { path: '/pos/delivery',       label: 'POS · Delivery',     group: 'kiosk'  },
  { path: '/pos/special',        label: 'POS · Special',      group: 'kiosk'  },
  { path: '/pos/bar',            label: 'POS · Bar',          group: 'kiosk'  },
  { path: '/kitchen',            label: 'Kitchen Display',    group: 'kiosk'  },
  { path: '/bar',                label: 'Bar Board',          group: 'kiosk'  },
  // Actions — capabilities, not pages. Enforced server-side too (see
  // TenantRolePermissions::canCancelOrders(), OrderController::cancel()).
  { path: 'action:cancel_order', label: 'Cancel orders / items', group: 'actions' },
];

/** Does this role grant default access to this path? Used to seed the
 *  checkboxes in the permissions panel. */
export function roleDefaultAccess(role: Role | undefined, path: string): boolean {
  if (!role) return false;
  const required = ACCESS[path];
  if (!required) return true;
  return required.includes(role);
}

/**
 * roleDefaultAccess() plus the single-role form of the owner auto-grant
 * carve-out in canAccess() (own-tenant /admin/* except /admin/commission).
 * Used by canAccess()'s per-role role_overrides resolution so a role with
 * no explicit tenant override gets exactly the same default it would get
 * without this feature at all — see the "not extend to /admin/commission"
 * rationale on the auto-grant above.
 */
function roleDefaultAccessWithOwnerGrant(role: string, path: string): boolean {
  if (role === 'owner' && path.startsWith('/admin') && path !== '/admin/commission') return true;
  return roleDefaultAccess(role as Role, path);
}

/** Human label for each role — used in chips, badges, dropdowns. */
export const ROLE_LABEL: Record<Role, string> = {
  platform_admin: 'Super Admin',
  super_admin:    'Super Admin',
  owner:          'Owner',
  admin:          'Admin',
  manager:        'Admin',     // old alias renders as 'Admin' visually
  cashier:        'Cashier',
  sales_person:   'Sales',
  procurement:    'Procurement',
  kitchen:        'Kitchen',
  accountant:     'Accountant',
  bar:            'Bar',
  investor:       'Investor',
};

/** Choices the New-employee dropdown offers (excludes identity-rooted roles). */
export const STAFF_ROLES: Array<{ id: Exclude<Role, 'platform_admin' | 'super_admin' | 'owner' | 'investor' | 'manager'>; label: string; hint: string }> = [
  { id: 'admin',        label: 'Admin',        hint: 'Branch administrator — staff, menu, orders' },
  { id: 'cashier',      label: 'Cashier',      hint: 'POS only — handles cash, rings up sales' },
  { id: 'sales_person', label: 'Sales',        hint: 'Floor staff — rings sales + views orders' },
  { id: 'procurement',  label: 'Procurement',  hint: 'Manages suppliers and purchase orders only' },
  { id: 'kitchen',      label: 'Kitchen',      hint: 'KDS only — sees tickets, marks ready' },
  { id: 'accountant',   label: 'Accountant',   hint: 'Reports, expenses, payouts — no POS' },
  { id: 'bar',          label: 'Bar',          hint: 'Bar board + POS, dashboard, records, procurement + expenses' },
];
